import { useMemo, useState, type FormEvent } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { usePreferences } from "../lib/preferences.js";
import { listBills } from "../lib/api-modules/bills.js";
import { listIncomeEvents } from "../lib/api-modules/income-events.js";
import { listSpendingTransactions } from "../lib/api-modules/spending-transactions.js";
import { getNetWorth } from "../lib/api-modules/networth.js";
import {
  createScenario,
  deleteScenario,
  listScenarios,
  updateScenario,
} from "../lib/api-modules/scenarios.js";
import type {
  CreateScenarioInput,
  Scenario,
  ScenarioDelta,
  ScenarioType,
} from "../lib/api-modules/types.js";
import { savingsRate } from "../lib/util/fire.js";
import {
  accountTaxMix,
  estimateRetirementTaxRatePct,
  fiAssetsCents as computeFiAssets,
} from "../lib/util/retirement-plan.js";
import { replayScenario } from "../lib/util/scenario-replay.js";
import { HISTORICAL_REAL_RETURNS_WITH_CAPE } from "../lib/data/historical-returns.js";
import {
  ALL_CITY_CODES,
  COL_INDEX_2026,
  type CityCode,
} from "../lib/data/cost-of-living.js";
import {
  SCENARIO_TEMPLATES,
  templateForKind,
} from "../lib/data/scenario-templates.js";
import {
  centsToDollars,
  dollarsToCents,
  formatCentsCompact,
} from "../lib/util/money.js";
import { useToast } from "../components/toast.js";
import {
  Button,
  Card,
  EmptyState,
  NumberField,
  Pill,
  SectionHeader,
  SelectField,
  TextField,
} from "../components/design-system/index.js";

import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";
/**
 * Phase 6 Session 4 — `<ScenariosPage>` at `/scenarios`.
 *
 * Save/load named what-ifs against the household's baseline FIRE
 * assumptions. Each scenario carries a typed delta (subset of
 * fire_assumptions overrides + scenario-specific knobs) that the
 * replay helper merges + runs through the 6.1/6.2/6.3 helpers.
 *
 * **Compute-on-render**: replay results are NOT persisted. Every
 * scenario card recomputes on render via `replayScenario()`. ~30-100ms
 * client-side per card.
 *
 * Layout:
 *   - Add scenario from template dropdown + button.
 *   - List of `<ScenarioCard>` components (active scenarios first;
 *     `?include_archived=true` shows archived).
 *   - Each card: name (inline edit) + replay results vs baseline +
 *     collapsible delta editor + archive button.
 */

const KIND_LABELS: Readonly<Record<ScenarioType, string>> = {
  job_loss: "Job loss",
  sabbatical: "Sabbatical",
  second_child: "Second child",
  relocation: "Relocation",
  emergency_50k: "$50K emergency",
  rate_change: "Rate change",
  custom: "Custom",
};

export function ScenariosPage() {
  const { user } = useAuth();
  const { privacyMode } = usePreferences();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [showArchived, setShowArchived] = useState(false);

  // === baseline data fetches ================================================
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const trailingFrom = useMemo(() => {
    const d = new Date(today);
    d.setUTCMonth(d.getUTCMonth() - 12);
    return d.toISOString().slice(0, 10);
  }, [today]);

  const networthQuery = useQuery({
    queryKey: ["networth", "24m" as const],
    queryFn: () => getNetWorth("24m"),
    enabled: !!user,
  });
  const billsQuery = useQuery({
    queryKey: ["bills", { includeArchived: false }],
    queryFn: () => listBills(false),
    enabled: !!user,
  });
  const incomeQuery = useQuery({
    queryKey: ["income-events", { from: trailingFrom }],
    queryFn: () => listIncomeEvents({ from: trailingFrom }),
    enabled: !!user,
  });
  const spendQuery = useQuery({
    queryKey: ["spending-transactions", { from: trailingFrom, to: today }],
    queryFn: () => listSpendingTransactions({ from: trailingFrom, to: today }),
    enabled: !!user,
  });

  const scenariosQuery = useQuery({
    queryKey: ["scenarios", { includeArchived: showArchived }],
    queryFn: () => listScenarios(showArchived),
    enabled: !!user,
  });

  // === baseline replay inputs ===============================================
  const baseline = useMemo(() => {
    const allReady =
      billsQuery.data &&
      incomeQuery.data &&
      spendQuery.data &&
      networthQuery.data;
    if (!allReady) return null;
    const sr = savingsRate({
      income_events: incomeQuery.data.income_events,
      bills: billsQuery.data.bills,
      spending_transactions: spendQuery.data.spending_transactions,
      today,
      lookback_months: 12,
    });
    const latest = networthQuery.data.latest;
    const annualSpendCents =
      user?.fire_assumptions?.annual_spend_override_cents ??
      sr.annualized_spend_cents;
    return {
      fiAssetsCents: latest ? computeFiAssets(latest) : 0,
      monthlyNetSavingsCents: sr.monthly_net_savings_cents,
      annualSpendCents,
      taxRatePct: estimateRetirementTaxRatePct({
        annual_spend_cents: annualSpendCents,
        mix: accountTaxMix(latest?.by_account_type),
      }),
    };
  }, [
    billsQuery.data,
    incomeQuery.data,
    spendQuery.data,
    networthQuery.data,
    user?.fire_assumptions?.annual_spend_override_cents,
    today,
  ]);

  // === add-from-template ====================================================
  const [pendingKind, setPendingKind] = useState<ScenarioType>("custom");

  const createMutation = useMutation({
    mutationFn: (input: CreateScenarioInput) => createScenario(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["scenarios"] });
      toast.show("Scenario created.");
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Failed to create";
      toast.show(msg);
    },
  });

  function onAddFromTemplate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const template = templateForKind(pendingKind);
    createMutation.mutate({
      name: template.default_name,
      scenario_type: template.kind,
      delta: template.default_delta,
    });
  }

  const scenarios = scenariosQuery.data?.scenarios ?? [];

  return (
    <div className="mx-auto max-w-5xl p-6 lg:p-8">
      <SectionHeader
        title="Scenarios"
        sub="Save and replay 'what if' moments — job loss, sabbatical, second child, relocation, emergency, rate change, custom."
        action={
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowArchived((v) => !v)}
              aria-pressed={showArchived}
            >
              {showArchived ? "Hide archived" : "Show archived"}
            </Button>
          </div>
        }
      />

      {/* Add scenario */}
      <Card className="mb-6" data-testid="scenarios-add">
        <form
          onSubmit={onAddFromTemplate}
          className="flex flex-wrap items-end gap-3"
        >
          <SelectField
            label="Add scenario from template"
            value={pendingKind}
            onChange={(e) => setPendingKind(e.target.value as ScenarioType)}
            containerClassName="min-w-[16rem]"
          >
            {SCENARIO_TEMPLATES.map((t) => (
              <option key={t.kind} value={t.kind}>
                {KIND_LABELS[t.kind]} — {t.default_name}
              </option>
            ))}
          </SelectField>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Adding…" : "Add scenario"}
          </Button>
        </form>
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          {templateForKind(pendingKind).narrative}
        </p>
      </Card>

      {scenariosQuery.isLoading ? (
        <p className="py-8 text-center text-sm text-[var(--color-muted)]">Loading…</p>
      ) : scenariosQuery.isError ? (
        <p className="py-8 text-center text-sm text-[var(--color-danger)]">
          Couldn't load scenarios.
        </p>
      ) : scenarios.length === 0 ? (
        <EmptyState
          title={showArchived ? "No archived scenarios" : "No scenarios yet"}
          description="Pick a template above and click 'Add scenario' to start exploring."
        />
      ) : !baseline ? (
        <p className="py-8 text-center text-sm text-[var(--color-muted)]">
          Loading baseline data…
        </p>
      ) : (
        <div className="space-y-4">
          {scenarios.map((s) => (
            <ScenarioCard
              key={s.scenario_id}
              scenario={s}
              baseline={baseline}
              today={today}
              hideValues={privacyMode}
            />
          ))}
        </div>
      )}
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.scenarios} />
      </div>
    </div>
  );
}

// ============================================================================
// Per-scenario card with replay + edit
// ============================================================================

interface BaselineInputs {
  fiAssetsCents: number;
  taxRatePct: number;
  monthlyNetSavingsCents: number;
  annualSpendCents: number;
}

function ScenarioCard({
  scenario,
  baseline,
  today,
  hideValues,
}: {
  readonly scenario: Scenario;
  readonly baseline: BaselineInputs;
  readonly today: string;
  readonly hideValues: boolean;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [expanded, setExpanded] = useState(false);

  // === replay (compute-on-render) ===========================================
  const replay = useMemo(
    () =>
      replayScenario({
        household_assumptions: user?.fire_assumptions ?? null,
        scenario: { delta: scenario.delta },
        helpers_input: {
          current_net_worth_cents: baseline.fiAssetsCents,
          monthly_net_savings_cents: baseline.monthlyNetSavingsCents,
          annual_spend_cents: baseline.annualSpendCents,
          today,
          trinity_returns: HISTORICAL_REAL_RETURNS_WITH_CAPE,
          effective_tax_rate_pct: baseline.taxRatePct,
        },
      }),
    [scenario.delta, user?.fire_assumptions, baseline, today],
  );

  const baselineReplay = useMemo(
    () =>
      replayScenario({
        household_assumptions: user?.fire_assumptions ?? null,
        scenario: { delta: {} },
        helpers_input: {
          current_net_worth_cents: baseline.fiAssetsCents,
          monthly_net_savings_cents: baseline.monthlyNetSavingsCents,
          annual_spend_cents: baseline.annualSpendCents,
          today,
          trinity_returns: HISTORICAL_REAL_RETURNS_WITH_CAPE,
          effective_tax_rate_pct: baseline.taxRatePct,
        },
      }),
    [user?.fire_assumptions, baseline, today],
  );

  const monthsDelta =
    replay.time_to_fi_months !== null && baselineReplay.time_to_fi_months !== null
      ? replay.time_to_fi_months - baselineReplay.time_to_fi_months
      : null;
  const trinityDelta =
    replay.trinity_success_pct - baselineReplay.trinity_success_pct;
  const mcDelta =
    replay.monte_carlo_p50_end_cents - baselineReplay.monte_carlo_p50_end_cents;

  const updateMutation = useMutation({
    mutationFn: (input: { name?: string; delta?: ScenarioDelta }) =>
      updateScenario(scenario.scenario_id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["scenarios"] });
      toast.show("Scenario saved.");
      setExpanded(false);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Failed to save";
      toast.show(msg);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => deleteScenario(scenario.scenario_id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["scenarios"] });
      toast.show("Scenario archived.");
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Failed to archive";
      toast.show(msg);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () =>
      updateScenario(scenario.scenario_id, { is_archived: false }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["scenarios"] });
      toast.show("Scenario restored.");
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Failed to restore";
      toast.show(msg);
    },
  });

  return (
    <Card data-testid={`scenario-card-${scenario.scenario_id}`}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <p className="font-display text-xl font-medium text-[var(--color-text)]">
            {scenario.name}
          </p>
          <Pill tone="neutral">{KIND_LABELS[scenario.scenario_type]}</Pill>
          {scenario.is_archived && <Pill tone="warning">archived</Pill>}
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {expanded ? "Close" : "Edit"}
          </Button>
          {scenario.is_archived ? (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => restoreMutation.mutate()}
              disabled={restoreMutation.isPending}
            >
              Restore
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => archiveMutation.mutate()}
              disabled={archiveMutation.isPending}
            >
              Archive
            </Button>
          )}
        </div>
      </div>

      {/* Replay deltas */}
      <div className="mb-3 grid gap-3 md:grid-cols-3">
        <DeltaTile
          label="Time-to-FI shift"
          value={
            monthsDelta === null
              ? "—"
              : monthsDelta === 0
                ? "no change"
                : monthsDelta > 0
                  ? `+${(monthsDelta / 12).toFixed(1)} yr`
                  : `−${(-monthsDelta / 12).toFixed(1)} yr`
          }
          sub={replay.time_to_fi_date ? `target ${replay.time_to_fi_date.slice(0, 7)}` : "beyond horizon"}
          tone={monthsDelta === null ? "neutral" : monthsDelta < 0 ? "success" : monthsDelta > 0 ? "warning" : "neutral"}
        />
        <DeltaTile
          label="Trinity success Δ"
          value={`${trinityDelta >= 0 ? "+" : ""}${trinityDelta.toFixed(1)} pp`}
          sub={`${replay.trinity_success_pct.toFixed(1)}% (vs ${baselineReplay.trinity_success_pct.toFixed(1)}%)`}
          tone={trinityDelta >= 0 ? "success" : "warning"}
        />
        <DeltaTile
          label="MC p50 end-balance Δ"
          value={
            hideValues
              ? "••••"
              : `${mcDelta >= 0 ? "+" : "−"}${formatCentsCompact(Math.abs(mcDelta), { hidden: false })}`
          }
          sub={
            hideValues
              ? ""
              : `${formatCentsCompact(replay.monte_carlo_p50_end_cents)} median`
          }
          tone={mcDelta >= 0 ? "success" : "warning"}
        />
      </div>

      {/* Delta summary line */}
      <div className="text-xs text-[var(--color-muted)]">
        <DeltaSummary delta={scenario.delta} />
      </div>

      {scenario.notes && (
        <p className="mt-2 text-xs italic text-[var(--color-muted)]">
          {scenario.notes}
        </p>
      )}

      {expanded && (
        <DeltaEditor
          scenario={scenario}
          onSave={(input) => updateMutation.mutate(input)}
          onCancel={() => setExpanded(false)}
          pending={updateMutation.isPending}
        />
      )}
    </Card>
  );
}

function DeltaTile({
  label,
  value,
  sub,
  tone,
}: {
  readonly label: string;
  readonly value: string;
  readonly sub: string;
  readonly tone: "neutral" | "success" | "warning";
}) {
  const toneClass =
    tone === "success"
      ? "text-[var(--color-success)]"
      : tone === "warning"
        ? "text-[var(--color-warning)]"
        : "text-[var(--color-text)]";
  return (
    <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-card)] p-3">
      <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
        {label}
      </p>
      <p className={`mt-1 font-mono text-2xl font-medium tabular-nums ${toneClass}`}>
        {value}
      </p>
      {sub && (
        <p className="mt-0.5 text-[11px] text-[var(--color-muted)]">{sub}</p>
      )}
    </div>
  );
}

function DeltaSummary({ delta }: { readonly delta: ScenarioDelta }) {
  const parts: string[] = [];
  if (delta.expected_return_rate_pct !== undefined) {
    parts.push(`return → ${delta.expected_return_rate_pct}%`);
  }
  if (delta.withdrawal_rate_pct !== undefined) {
    parts.push(`wr → ${delta.withdrawal_rate_pct}%`);
  }
  if (delta.annual_spend_override_cents !== undefined) {
    parts.push(`annual spend → $${(delta.annual_spend_override_cents / 100).toLocaleString()}`);
  }
  if (delta.annual_spend_delta_cents !== undefined) {
    parts.push(`spend Δ ${delta.annual_spend_delta_cents >= 0 ? "+" : "−"}$${Math.abs(delta.annual_spend_delta_cents / 100).toLocaleString()}/yr`);
  }
  if (delta.monthly_net_savings_override_cents !== undefined) {
    parts.push(`monthly savings → $${(delta.monthly_net_savings_override_cents / 100).toLocaleString()}`);
  }
  if (delta.portfolio_one_time_delta_cents !== undefined) {
    parts.push(`portfolio Δ ${delta.portfolio_one_time_delta_cents >= 0 ? "+" : "−"}$${Math.abs(delta.portfolio_one_time_delta_cents / 100).toLocaleString()}`);
  }
  if (delta.relocate_to_city_code) {
    const city = COL_INDEX_2026[delta.relocate_to_city_code as CityCode];
    parts.push(`move to ${city ? city.city : delta.relocate_to_city_code}`);
  }
  if (delta.duration_months) {
    parts.push(`for ${delta.duration_months} months`);
  }
  return parts.length === 0
    ? <span>(empty delta — runs at baseline)</span>
    : <span>{parts.join(" · ")}</span>;
}

function DeltaEditor({
  scenario,
  onSave,
  onCancel,
  pending,
}: {
  readonly scenario: Scenario;
  readonly onSave: (input: { name?: string; delta?: ScenarioDelta }) => void;
  readonly onCancel: () => void;
  readonly pending: boolean;
}) {
  const [name, setName] = useState(scenario.name);
  const [returnPct, setReturnPct] = useState(
    scenario.delta.expected_return_rate_pct !== undefined
      ? String(scenario.delta.expected_return_rate_pct)
      : "",
  );
  const [wrPct, setWrPct] = useState(
    scenario.delta.withdrawal_rate_pct !== undefined
      ? String(scenario.delta.withdrawal_rate_pct)
      : "",
  );
  const [spendOverride, setSpendOverride] = useState(
    scenario.delta.annual_spend_override_cents !== undefined
      ? centsToDollars(scenario.delta.annual_spend_override_cents)
      : "",
  );
  const [spendDelta, setSpendDelta] = useState(
    scenario.delta.annual_spend_delta_cents !== undefined
      ? centsToDollars(scenario.delta.annual_spend_delta_cents)
      : "",
  );
  const [savingsOverride, setSavingsOverride] = useState(
    scenario.delta.monthly_net_savings_override_cents !== undefined
      ? centsToDollars(scenario.delta.monthly_net_savings_override_cents)
      : "",
  );
  const [portfolioJolt, setPortfolioJolt] = useState(
    scenario.delta.portfolio_one_time_delta_cents !== undefined
      ? centsToDollars(scenario.delta.portfolio_one_time_delta_cents)
      : "",
  );
  const [duration, setDuration] = useState(
    scenario.delta.duration_months !== undefined
      ? String(scenario.delta.duration_months)
      : "",
  );
  const [relocateTo, setRelocateTo] = useState(
    scenario.delta.relocate_to_city_code ?? "",
  );

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const delta: ScenarioDelta = {};
    if (returnPct !== "") delta.expected_return_rate_pct = Number(returnPct);
    if (wrPct !== "") delta.withdrawal_rate_pct = Number(wrPct);
    if (spendOverride !== "")
      delta.annual_spend_override_cents = dollarsToCents(spendOverride);
    if (spendDelta !== "")
      delta.annual_spend_delta_cents = dollarsToCents(spendDelta);
    if (savingsOverride !== "")
      delta.monthly_net_savings_override_cents = dollarsToCents(savingsOverride);
    if (portfolioJolt !== "")
      delta.portfolio_one_time_delta_cents = dollarsToCents(portfolioJolt);
    if (duration !== "") delta.duration_months = Number(duration);
    if (relocateTo !== "") delta.relocate_to_city_code = relocateTo;
    onSave({ name, delta });
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mt-4 rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-elevated)] p-4"
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          containerClassName="lg:col-span-3"
        />
        <NumberField
          label="Expected return %"
          helper="overrides household default"
          value={returnPct}
          onChange={(e) => setReturnPct(e.target.value)}
          step="0.1"
          min="-20"
          max="50"
          placeholder=""
        />
        <NumberField
          label="Withdrawal rate %"
          helper="overrides household default"
          value={wrPct}
          onChange={(e) => setWrPct(e.target.value)}
          step="0.1"
          min="0.1"
          max="20"
          placeholder=""
        />
        <NumberField
          label="Annual spend override ($)"
          helper="hard set"
          value={spendOverride}
          onChange={(e) => setSpendOverride(e.target.value)}
          step="0.01"
          min="0"
          placeholder=""
        />
        <NumberField
          label="Annual spend delta ($)"
          helper="add to baseline"
          value={spendDelta}
          onChange={(e) => setSpendDelta(e.target.value)}
          step="0.01"
          placeholder=""
        />
        <NumberField
          label="Monthly savings override ($)"
          helper="0 for job loss / sabbatical"
          value={savingsOverride}
          onChange={(e) => setSavingsOverride(e.target.value)}
          step="0.01"
          min="0"
          placeholder=""
        />
        <NumberField
          label="Portfolio one-time delta ($)"
          helper="negative for losses"
          value={portfolioJolt}
          onChange={(e) => setPortfolioJolt(e.target.value)}
          step="0.01"
          placeholder=""
        />
        <NumberField
          label="Duration (months)"
          helper="documentation"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          step="1"
          min="0"
          placeholder=""
        />
        <SelectField
          label="Relocate to"
          value={relocateTo}
          onChange={(e) => setRelocateTo(e.target.value)}
        >
          <option value="">(no move)</option>
          {ALL_CITY_CODES.map((c) => (
            <option key={c} value={c}>
              {COL_INDEX_2026[c].city}, {COL_INDEX_2026[c].country}
            </option>
          ))}
        </SelectField>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={pending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={pending}
        >
          {pending ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}

